/**
 * Derivative reconciliation engine.
 *
 * JavaScript port of recon-dashboard/api/recon_logic.py, which is the source
 * of truth for this logic. Function names, result keys and tolerances are kept
 * identical so results can be diffed against the Python implementation
 * directly. The only difference is that everything runs in the browser on
 * files parsed by src/api/derivativeRecon.js instead of on the server.
 *
 * Behaviour is pinned by src/utils/__tests__/derivativeReconLogic.test.js,
 * ported from the pytest suites in that repo.
 */

import { extractEntity, getVal, stripSuffix, toDateObj } from './derivativeReconFormat';

export const TOLERANCE_VAL = 50;
export const TOLERANCE_QTY = 1;
export const CASH_TOL = 5;
const BDAY_WINDOW = 5;

const CW_DATE_CUTOFF = new Date(2025, 7, 31); // Aug 31 2025
const TX_DATE_CUTOFF = new Date(2025, 0, 1);  // Jan 1 2025

/** CW position identifiers that are known noise and never reconcile. */
const CW_POS_EXCLUDE_IDS = new Set(['665279873']);

/**
 * Two candidate matches within this many dollars of each other are treated as
 * equally good. Amounts arrive as floats, so exact equality would miss ties
 * that differ only in the last bit.
 */
const TIE_EPS = 1e-6;

/** Bounds on the joint re-allocation that runs when a wire is left stranded. */
const COMPONENT_CAP = 64;      // wires reshuffled together
const SUBSET_CAP = 64;         // candidate subsets offered per wire
const ALLOC_BUDGET = 200_000;  // nodes searched before keeping the greedy answer
const PROBE_PER_WIRE = 1_000_000; // nodes one wire's candidate list may cost

/**
 * Ceiling on subset-search work, in nodes visited per custody row.
 *
 * The search is exponential in the candidate count and runs in the browser
 * here rather than on a server, so it needs a bound. Bounding *work* rather
 * than truncating the candidate list keeps the search exact: dropping
 * candidates can discard the only valid subset (21 flows of 1,000–1,200
 * against a 2,010 wire needs the two smallest), which would report false cash
 * breaks. Realistic groups finish orders of magnitude inside this.
 */
const WIRE_SEARCH_BUDGET = 2_000_000;

function num(v) {
  if (v == null) return 0;
  if (typeof v === 'number' && !isNaN(v)) return v;
  const s = String(v).replace(/,/g, '').replace(/\$/g, '').trim();
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function normalizeDate(v) {
  if (v == null) return null;
  const d = toDateObj(v);
  if (!d) return null;
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

/** Local-calendar YYYY-MM-DD, matching Python's naive `date().isoformat()`. */
function dateKey(d) {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function coalesce(...vals) {
  for (const v of vals) {
    if (v != null && v !== '') return v;
  }
  return null;
}

function strOrEmpty(v) {
  return v == null ? '' : String(v);
}

/** Normalise a raw ID, falling back to the raw value when it has no digits. */
function normalizeId(raw) {
  return stripSuffix(raw) || raw;
}

const COL = {
  CW_POS_ID: 'Identifier',
  CW_POS_VAL: 'Notional Value',
  CW_POS_QTY: 'Base Current Units',
  CW_POS_STRIKE: 'Strike Price',
  CW_POS_TRADE_DT: 'Trade Date',
  CW_POS_SETTLE_DT: 'Settle Date',
  CW_POS_ACCT: 'Account',
  CW_TX_ID: 'Identifier',
  CW_TX_AMT: 'Base Cash Affecting Amount',
  CW_TX_ACCT: 'Account',
  MIL_ID: 'Trade ID',
  MIL_VAL: 'Notional',
  MIL_QTY: 'Number of Options',
  MIL_STRIKE: 'Lower strike',
  MIL_TRADE_DT: 'Trade Date',
  MIL_SETTLE_DT: 'Settlement Date',
  MIL_PAYOFF_DT: 'Payoff Date',
  MIL_EXP_DT: 'Expiration Date',
  MIL_ACCT: 'Account',
  MIL_BUYER: 'Buyer',
  MIL_BROKER: 'Broker',
  MIL_PREM_AMT: 'Total Premium',
  MIL_PAY_AMT: 'Payoff',
};

export function reconcilePositions(milRows, cwRows) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const milMap = {};
  for (const r of milRows) {
    const rawRid = strOrEmpty(getVal(r, COL.MIL_ID)).trim();
    const rid = normalizeId(rawRid);
    if (!rid || rid.toLowerCase() === 'undefined') continue;
    const expRaw = getVal(r, COL.MIL_EXP_DT);
    if (expRaw != null) {
      const ed = toDateObj(expRaw);
      if (ed && ed < now) continue;
    }
    if (!milMap[rid]) {
      milMap[rid] = {
        cpName: String(
          coalesce(getVal(r, COL.MIL_BROKER), getVal(r, COL.MIL_BUYER), getVal(r, COL.MIL_ACCT)) || 'Unknown'
        ),
        entity: extractEntity(getVal(r, COL.MIL_BUYER)),
        milVal: 0,
        milQty: 0,
        milStrike: num(getVal(r, COL.MIL_STRIKE)),
        milTrade: normalizeDate(getVal(r, COL.MIL_TRADE_DT)),
        milSettle: normalizeDate(getVal(r, COL.MIL_SETTLE_DT)),
        milExp: normalizeDate(getVal(r, COL.MIL_EXP_DT)),
      };
    }
    milMap[rid].milVal += num(getVal(r, COL.MIL_VAL));
    milMap[rid].milQty += num(getVal(r, COL.MIL_QTY));
  }

  const cwMap = {};
  for (const cr of cwRows) {
    const raw = strOrEmpty(getVal(cr, COL.CW_POS_ID)).trim();
    const norm = stripSuffix(raw);
    if (!norm) continue;
    if (CW_POS_EXCLUDE_IDS.has(raw) || CW_POS_EXCLUDE_IDS.has(norm)) continue;
    if (!cwMap[norm]) {
      cwMap[norm] = {
        _rawId: raw,
        cwVal: 0,
        cwQty: 0,
        cwStrike: num(getVal(cr, COL.CW_POS_STRIKE)),
        cwTrade: normalizeDate(getVal(cr, COL.CW_POS_TRADE_DT)),
        cwSettle: normalizeDate(getVal(cr, COL.CW_POS_SETTLE_DT)),
        cpName: String(getVal(cr, COL.CW_POS_ACCT) || 'Unknown'),
      };
    }
    cwMap[norm].cwVal += num(getVal(cr, COL.CW_POS_VAL));
    cwMap[norm].cwQty += num(getVal(cr, COL.CW_POS_QTY));
  }

  const results = [];
  const matched = new Set();

  for (const [rid, mil] of Object.entries(milMap)) {
    const cw = cwMap[rid];
    if (!cw) {
      results.push({
        id: rid, rawCwId: null, cpName: mil.cpName, entity: mil.entity,
        milVal: mil.milVal, milQty: mil.milQty, milStrike: mil.milStrike,
        milTrade: mil.milTrade, milSettle: mil.milSettle, milExp: mil.milExp,
        cwVal: null, cwQty: null, cwStrike: null, cwTrade: null, cwSettle: null,
        valDiff: null, qtyDiff: null, strikeDiff: null,
        tradeDtMatch: null, settleDtMatch: null, status: 'MISSING_BOR',
      });
      continue;
    }
    matched.add(rid);
    const vd = mil.milVal - cw.cwVal;
    const qd = mil.milQty - cw.cwQty;
    const sd = (mil.milStrike || 0) - (cw.cwStrike || 0);
    const tradeDo = toDateObj(mil.milTrade);
    const settleDo = toDateObj(mil.milSettle);
    let tm = null;
    let sm = null;
    if (mil.milTrade && cw.cwTrade && tradeDo && tradeDo >= CW_DATE_CUTOFF) {
      tm = mil.milTrade === cw.cwTrade;
    }
    if (mil.milSettle && cw.cwSettle && settleDo && settleDo >= CW_DATE_CUTOFF) {
      sm = mil.milSettle === cw.cwSettle;
    }
    const brk = Math.abs(vd) > TOLERANCE_VAL || Math.abs(qd) >= TOLERANCE_QTY || Math.abs(sd) > TOLERANCE_VAL;
    const st = !brk ? 'MATCHED' : (Math.abs(vd) > TOLERANCE_VAL || Math.abs(sd) > TOLERANCE_VAL ? 'VAL_BREAK' : 'QTY_BREAK');
    results.push({
      id: rid, rawCwId: cw._rawId, cpName: mil.cpName, entity: mil.entity,
      milVal: mil.milVal, milQty: mil.milQty, milStrike: mil.milStrike,
      milTrade: mil.milTrade, milSettle: mil.milSettle, milExp: mil.milExp,
      cwVal: cw.cwVal, cwQty: cw.cwQty, cwStrike: cw.cwStrike,
      cwTrade: cw.cwTrade, cwSettle: cw.cwSettle,
      valDiff: vd, qtyDiff: qd, strikeDiff: sd,
      tradeDtMatch: tm, settleDtMatch: sm, status: st,
    });
  }

  for (const [norm, cw] of Object.entries(cwMap)) {
    if (matched.has(norm)) continue;
    results.push({
      id: norm, rawCwId: cw._rawId, cpName: cw.cpName, entity: null,
      milVal: null, milQty: null, milStrike: null,
      milTrade: null, milSettle: null, milExp: null,
      cwVal: cw.cwVal, cwQty: cw.cwQty, cwStrike: cw.cwStrike,
      cwTrade: cw.cwTrade, cwSettle: cw.cwSettle,
      valDiff: null, qtyDiff: null, strikeDiff: null,
      tradeDtMatch: null, settleDtMatch: null, status: 'MISSING_CP',
    });
  }

  return results;
}

/**
 * Reconcile Milliman transaction amounts against Clearwater cash transactions.
 *
 * `cwTxTypeFilter` accepts either a single Transaction Type or a list of them;
 * payoffs pass ['Option Settle', 'Expiration'] so both CW spellings pool into
 * one row per trade rather than producing a phantom break pair.
 */
export function reconcileTx(milRows, cwTxRows, milAmtCol, milDateCol, cwTxTypeFilter) {
  const allowedTypes = new Set(
    (Array.isArray(cwTxTypeFilter) ? cwTxTypeFilter : [cwTxTypeFilter])
      .map((t) => String(t).toLowerCase())
  );

  const milMap = {};
  for (const r of milRows) {
    const rawRid = strOrEmpty(getVal(r, COL.MIL_ID)).trim();
    const rid = normalizeId(rawRid);
    if (!rid) continue;
    const dr = getVal(r, milDateCol);
    if (dr != null) {
      const dd = toDateObj(dr);
      if (dd && dd < TX_DATE_CUTOFF) continue;
    }
    if (!milMap[rid]) {
      milMap[rid] = {
        total: 0,
        cpName: String(
          coalesce(getVal(r, COL.MIL_BROKER), getVal(r, COL.MIL_BUYER), getVal(r, COL.MIL_ACCT)) || 'Unknown'
        ),
        entity: extractEntity(getVal(r, COL.MIL_BUYER)),
        settle: normalizeDate(dr),
      };
    }
    milMap[rid].total += num(getVal(r, milAmtCol));
  }

  const cwMap = {};
  for (const cr of cwTxRows) {
    const tx = strOrEmpty(getVal(cr, 'Transaction Type')).trim().toLowerCase();
    if (!allowedTypes.has(tx)) continue;
    const csr = coalesce(
      getVal(cr, 'Base Settle Date'),
      getVal(cr, 'Settlement Date'),
      getVal(cr, 'Settle Date'),
    );
    if (csr != null) {
      const cd = toDateObj(csr);
      if (cd && cd < TX_DATE_CUTOFF) continue;
    }
    const raw2 = strOrEmpty(getVal(cr, COL.CW_TX_ID)).trim();
    const norm2 = stripSuffix(raw2);
    if (!norm2) continue;
    if (!cwMap[norm2]) {
      cwMap[norm2] = {
        total: 0,
        cpName: String(getVal(cr, COL.CW_TX_ACCT) || 'Unknown'),
        settle: normalizeDate(csr),
      };
    }
    cwMap[norm2].total += Math.abs(num(getVal(cr, COL.CW_TX_AMT)));
  }

  const results = [];
  const matched = new Set();

  for (const [rid, mil] of Object.entries(milMap)) {
    const cw = cwMap[rid];
    if (!cw) {
      results.push({
        id: rid, cpName: mil.cpName, entity: mil.entity, settle: mil.settle,
        milTotal: mil.total, cwTotal: null, diff: null, status: 'MISSING_BOR',
      });
      continue;
    }
    matched.add(rid);
    const diff = mil.total - cw.total;
    results.push({
      id: rid, cpName: mil.cpName, entity: mil.entity,
      settle: mil.settle || cw.settle,
      milTotal: mil.total, cwTotal: cw.total, diff,
      status: Math.abs(diff) > TOLERANCE_VAL ? 'VAL_BREAK' : 'MATCHED',
    });
  }

  for (const [norm, cw] of Object.entries(cwMap)) {
    if (matched.has(norm)) continue;
    results.push({
      id: norm, cpName: cw.cpName, entity: null, settle: cw.settle,
      milTotal: null, cwTotal: cw.total, diff: null, status: 'MISSING_CP',
    });
  }

  return results;
}

/**
 * One flow per (tradeId, broker, buyer, date).
 *
 * Multiple Milliman rows for the same trade on the same day at the same broker
 * still aggregate, but distinct trades stay distinct so the matcher can pair
 * each trade to its own wire when the broker settled net-per-trade instead of
 * gross. Rows with no trade ID key off their row index so they never
 * accidentally aggregate with each other.
 */
export function buildMillimanCashFlows(premRows, payRows) {
  const buildMap = (rows, { dateCols, amtCol, type }) => {
    const map = new Map();
    rows.forEach((r, idx) => {
      const dr = coalesce(...dateCols.map((c) => getVal(r, c)));
      if (!dr) return;
      const d = toDateObj(dr);
      if (!d) return;
      const amt = num(getVal(r, amtCol));
      if (!amt) return;
      const broker = String(
        coalesce(getVal(r, COL.MIL_BROKER), getVal(r, COL.MIL_BUYER)) || 'Unknown'
      );
      const rawTid = String(getVal(r, COL.MIL_ID) ?? '').trim();
      const tid = normalizeId(rawTid);
      const buyer = String(getVal(r, COL.MIL_BUYER) ?? '');
      const key = `${dateKey(d)}|${broker}|${buyer}|${tid || `__row${idx}`}`;
      if (!map.has(key)) {
        map.set(key, {
          date: d, dateStr: normalizeDate(dr), broker, type,
          total: 0, entity: buyer, tradeIds: [],
        });
      }
      const entry = map.get(key);
      entry.total += amt;
      if (tid && !entry.tradeIds.includes(tid)) entry.tradeIds.push(tid);
    });
    return map;
  };

  const premMap = buildMap(premRows, {
    dateCols: [COL.MIL_SETTLE_DT], amtCol: COL.MIL_PREM_AMT, type: 'Premium',
  });
  const payMap = buildMap(payRows, {
    dateCols: [COL.MIL_PAYOFF_DT, COL.MIL_SETTLE_DT], amtCol: COL.MIL_PAY_AMT, type: 'Payoff',
  });

  const flows = [
    ...[...premMap.values()].filter((v) => Math.abs(v.total) > 0.01),
    ...[...payMap.values()].filter((v) => Math.abs(v.total) > 0.01),
  ];
  flows.sort((a, b) => b.date - a.date);
  return flows;
}

export function parseCustodyFile(rows, source) {
  const out = [];
  for (const r of rows) {
    let dv, amt, desc;
    if (source === 'NT') {
      dv = getVal(r, 'D-GL-POST');
      amt = num(getVal(r, 'Net amount - local'));
      desc = String(getVal(r, 'narrative') ?? '');
    } else {
      dv = getVal(r, 'Value Date');
      amt = num(getVal(r, 'Net Amount'));
      desc = String(getVal(r, 'Additional Information') ?? '');
    }
    const acct = getVal(r, 'Account Number');
    const acctNum = acct == null ? '' : String(acct).trim();
    const d = toDateObj(dv);
    if (!d) continue;
    out.push({ date: d, dateStr: normalizeDate(dv), amt, desc, source, acctNum });
  }
  return out;
}

// ─── Wire bundling ──────────────────────────────────────────────────────────

function siblingSummary(members) {
  return members.map((m) => ({
    broker: m.broker,
    entity: m.entity ?? null,
    type: m.type,
    total: m.total,
    tradeIds: [...(m.tradeIds || [])],
    dateStr: m.dateStr ?? null,
  }));
}

function allTradeIds(members) {
  const out = [];
  for (const m of members) {
    for (const tid of m.tradeIds || []) {
      if (tid && !out.includes(tid)) out.push(tid);
    }
  }
  return out;
}

/**
 * First combination of exactly `size` entries of `pool` whose absolute totals
 * sum to `targetAbs` within CASH_TOL, or null.
 *
 * Explores indices in ascending order, so it returns the same combination a
 * plain lexicographic enumeration would. Branches whose running sum already
 * overshoots are abandoned — sound, because every flow amount is positive, so
 * adding more can only increase the sum. `budget` is shared across the whole
 * search; exhausting it aborts and yields null rather than freezing the tab.
 */
/**
 * Every subset of `pool` of exactly `size` whose absolute totals reach
 * `targetAbs`, within the shared search budget.
 *
 * All of them, not the first couple: the flows that appear in some valid
 * bundle are exactly the flows the wire might have settled, and stopping early
 * made that set depend on the order the walk happened to reach them in. The
 * budget still bounds the work; an exhausted search is reported by the caller.
 */
/**
 * Flatten a list of subsets into the distinct flow indices they cover.
 *
 * A loop, not `[].concat(...subsets)`: a wire with twenty interchangeable
 * flows has 184,756 equal bundles, and spreading that many arrays as call
 * arguments overflows the stack.
 */
function flowsIn(subsets) {
  const out = new Set();
  for (const subset of subsets) for (const i of subset) out.add(i);
  return [...out];
}

/** A flow's identity by content, so choices survive a reordered input. */
function flowContentKey(flows, i) {
  const f = flows[i];
  return [(f.tradeIds || []).join(','), f.entity || '', f.broker || '', f.total].join('|');
}

/** The subset a tie resolves to: smallest by content, never by position. */
function pickRepresentative(flows, subsets) {
  let best = null;
  let bestKey = null;
  for (const sub of subsets) {
    const key = sub.map((i) => flowContentKey(flows, i)).sort().join('~');
    if (bestKey === null || key < bestKey) { bestKey = key; best = sub; }
  }
  return best;
}

function scanSubsetsOfSize(flows, pool, size, targetAbs, budget, onFound) {
  const chosen = [];
  // Returns true to unwind: the budget is spent, or the caller has seen enough.
  const walk = (start, remaining, sum) => {
    if (remaining === 0) {
      if (Math.abs(sum - targetAbs) > CASH_TOL) return false;
      return onFound(chosen) === false;
    }
    const limit = budget.limit || WIRE_SEARCH_BUDGET;
    for (let i = start; i <= pool.length - remaining; i += 1) {
      if (budget.steps >= limit) return true;
      budget.steps += 1;
      const next = sum + Math.abs(flows[pool[i]].total);
      // Unsorted amounts, so skip this branch rather than breaking out.
      if (next - targetAbs > CASH_TOL) continue;
      chosen.push(pool[i]);
      const stop = walk(i + 1, remaining - 1, next);
      chosen.pop();
      if (stop) return true;
    }
    return false;
  };
  walk(0, size, 0);
}

/**
 * Find a subset of `avail` flow indices whose absolute totals sum to
 * `targetAbs` within `tol`.
 *
 * Prefers single-flow matches (closest amount first), then smallest subsets.
 * Subset matches always use CASH_TOL even when the single-flow tolerance is
 * wider, since a multi-flow sum drifting by tens of dollars is coincidence,
 * not a fee.
 */
function findWireSubset(flows, avail, targetAbs, tol) {
  // Every flow the same distance from the wire is an equally good answer. Keep
  // all of them: picking the first is picking by input order, which is how a
  // reversed file used to change which trade was reported as settled.
  let bestD = null;
  let singles = [];
  for (const i of avail) {
    const d = Math.abs(Math.abs(flows[i].total) - targetAbs);
    if (d > tol) continue;
    if (bestD === null || d < bestD - TIE_EPS) {
      bestD = d;
      singles = [i];
    } else if (Math.abs(d - bestD) <= TIE_EPS) {
      singles.push(i);
    }
  }
  const sidesOf = (sums) => ({
    over: sums.some((v) => v > targetAbs + TIE_EPS),
    under: sums.some((v) => v < targetAbs - TIE_EPS),
  });
  if (singles.length >= 1) {
    const sides = sidesOf(singles.map((i) => Math.abs(flows[i].total)));
    if (singles.length === 1) return { subset: singles, gap: bestD, ...sides };
    return {
      tied: true,
      subset: pickRepresentative(flows, singles.map((i) => [i])),
      pool: singles,
      gap: bestD,
      ...sides,
    };
  }

  if (avail.length < 2) return null;

  // Sound prune: every flow amount is positive, so any flow larger than the
  // wire (plus tolerance) cannot appear in a subset that sums to it. Dropping
  // these cannot change which subset is found — it only shrinks the search.
  const pool = avail.filter((i) => Math.abs(flows[i].total) <= targetAbs + CASH_TOL);
  if (pool.length < 2) return null;

  const budget = { steps: 0 };
  // Every size the pool can make. A fixed ceiling of 12 meant a broker who
  // settled thirteen trades in one payment had the whole bundle reported as
  // breaks; the work budget below is the real bound, and it bounds the total
  // across sizes rather than guessing at how many trades a wire may carry.
  for (let size = 2; size <= pool.length; size += 1) {
    // Accumulated, never collected. Twenty 10k flows against a 100k wire have
    // 184,756 equal bundles: holding them cost 238MB and half a minute, and
    // all that is needed of them is which flows they cover and one
    // representative, both of which fold in as they are found.
    let hits = 0;
    let representative = null;
    let repKey = null;
    let bestGap = null;
    let over = false;
    let under = false;
    let covered = new Set();
    scanSubsetsOfSize(flows, pool, size, targetAbs, budget, (combo) => {
      // Bundles of one size do not all reach the wire equally: against a $100
      // wire, 70+30 is exact and 61+44 is $5 out, and both are inside the
      // tolerance. Only the closest are answers -- keeping whichever sorted
      // first could hand the wire to a $103 bundle and report the flows that
      // add up perfectly as breaks.
      const sum = combo.reduce((acc, i) => acc + Math.abs(flows[i].total), 0);
      const gap = Math.abs(sum - targetAbs);
      if (bestGap !== null && gap > bestGap + TIE_EPS) return true;
      if (bestGap === null || gap < bestGap - TIE_EPS) {
        bestGap = gap;
        hits = 0;
        repKey = null;
        representative = null;
        covered = new Set();
        over = false;
        under = false;
      }
      // Which side of the wire this bundle lands, judged on its TOTAL. Every
      // flow inside a bundle is smaller than the wire, so asking the parts
      // said "straddles" for bundles that all overshoot together.
      if (sum > targetAbs + TIE_EPS) over = true;
      if (sum < targetAbs - TIE_EPS) under = true;
      hits += 1;
      for (const i of combo) covered.add(i);
      const key = combo.map((i) => flowContentKey(flows, i)).sort().join('~');
      if (repKey === null || key < repKey) {
        repKey = key;
        representative = [...combo];
      }
      return true;
    });
    // A partial enumeration is not a tie, it is an unknown: fall through to the
    // budget check below rather than calling an arbitrary prefix the candidates.
    if (budget.steps < WIRE_SEARCH_BUDGET) {
      if (hits === 1) return { subset: representative, gap: bestGap, over, under };
      if (hits > 1) {
        return { tied: true, subset: representative, pool: [...covered], gap: bestGap, over, under };
      }
    }
    if (budget.steps >= WIRE_SEARCH_BUDGET) {
      // Give up rather than freeze, but say so: an exhausted search reports
      // the same MISSING_BOR rows as a genuine no-match, and without this the
      // two are indistinguishable after the fact. Realistic groups finish
      // orders of magnitude inside the budget (a 50-flow group settles in
      // ~56ms), so this firing at all means the input is worth a look.
      // eslint-disable-next-line no-console
      console.warn(
        '[derivativeRecon] wire subset search exhausted its budget; '
        + `${pool.length} candidate flows against ${targetAbs}. `
        + 'Any resulting breaks for this group may be spurious.',
      );
      return null;
    }
  }
  return null;
}

/**
 * Bundle Milliman flows into custody wires.
 *
 * A wire is one custody row matched to a subset of flows on the same date and
 * broker whose absolute amounts sum to the custody amount. Each result row
 * carries a wireId plus sibling info so the UI can show what else rode on that
 * wire. Runs a strict pass at CASH_TOL first so exact matches claim their
 * flows before a wider TOLERANCE_VAL sweep picks up the remainder.
 */
export function reconcileCash(milFlows, custodyRows) {
  const emptyWire = {
    status: 'MISSING_BOR', ambiguous: false, diff: null,
    wireId: null, siblingFlows: [], siblingTradeIds: [],
  };
  if (!custodyRows || !custodyRows.length) {
    return milFlows.map((m) => ({ mil: m, custody: null, ...emptyWire }));
  }

  const flowUsed = new Array(milFlows.length).fill(false);
  // "Nothing taken yet", for asking what a wire could have matched in principle.
  const NONE_USED = new Array(milFlows.length).fill(false);
  const custodyUsed = new Array(custodyRows.length).fill(false);
  const windowMs = (BDAY_WINDOW * 7 / 5) * 86400000;

  const flowTimes = milFlows.map((f) => f.date.getTime());
  const custodyTimes = custodyRows.map((c) => c.date.getTime());

  // Insertion-ordered so ties resolve the same way Python's dict iteration does.
  const flowGroups = new Map();
  milFlows.forEach((f, fi) => {
    const key = `${(f.broker || '').toLowerCase().trim()}|${dateKey(f.date)}|${f.type || ''}`;
    if (!flowGroups.has(key)) flowGroups.set(key, { type: f.type || '', broker: (f.broker || '').toLowerCase().trim(), idxs: [] });
    flowGroups.get(key).idxs.push(fi);
  });

  /**
   * Best subset for custody row *ci*, or an ambiguity.
   *
   * Returns ``{subset}`` for a single best answer, ``{ambiguous, candidates}``
   * when two or more are equally good, and null for no match. Ties used to be
   * settled by whichever group the flows happened to be inserted in, so the
   * same book in a different order named a different trade as settled.
   */
  const attemptMatch = (ci, tol, used = flowUsed) => {
    const c = custodyRows[ci];
    const cAbs = Math.abs(c.amt);
    const cTime = custodyTimes[ci];
    const cDescL = (c.desc || '').toLowerCase();
    const wireType = c.amt > 0 ? 'Payoff' : 'Premium';
    let bestScore = null;
    let top = [];

    for (const group of flowGroups.values()) {
      if (group.type !== wireType) continue;
      const avail = group.idxs.filter((i) => !used[i]);
      if (!avail.length) continue;
      if (Math.abs(flowTimes[avail[0]] - cTime) > windowMs) continue;
      const found = findWireSubset(milFlows, avail, cAbs, tol);
      if (!found) continue;
      const brokerMatch = Boolean(group.broker) && cDescL.includes(group.broker);
      const score = (brokerMatch ? 100 : 0) - found.subset.length;
      // How far out this answer is, as reported by the search rather than
      // recomputed from one representative: within a group the candidates are
      // already the closest ones. It decides between equally scored groups,
      // without which a $10 candidate and a $40 one were pooled as a tie and
      // the reported difference came down to which trade ID sorted first.
      if (found.gap == null) {
        found.gap = Math.abs(
          found.subset.reduce((acc, i) => acc + Math.abs(milFlows[i].total), 0) - cAbs,
        );
      }
      if (bestScore === null || score > bestScore
        || (score === bestScore && found.gap < top[0].gap - TIE_EPS)) {
        bestScore = score;
        top = [found];
      } else if (score === bestScore && Math.abs(found.gap - top[0].gap) <= TIE_EPS) {
        top.push(found);
      }
    }

    if (!top.length) return null;
    if (top.length === 1 && !top[0].tied) return { subset: top[0].subset };
    // The wire settled one of these and the data does not say which, so carry
    // the flows any equally good answer could have used along with the one
    // representative the tie resolves to.
    const pool = new Set();
    for (const t of top) for (const i of (t.pool || t.subset)) pool.add(i);
    return {
      ambiguous: true,
      subset: pickRepresentative(milFlows, top.map((t) => t.subset)),
      pool: [...pool],
      // Straddling only if some equally good answer lands above the wire and
      // another below it.
      straddles: top.some((t) => t.over) && top.some((t) => t.under),
    };
  };

  const custodyOrder = custodyRows
    .map((_, i) => i)
    .sort((a, b) => custodyTimes[a] - custodyTimes[b]);

  /**
   * A key derived from the flow's own contents, so the choice between
   * interchangeable candidates survives the input arriving in another order.
   */
  const flowKey = (i) => {
    const f = milFlows[i];
    return [
      (f.tradeIds || []).join(','), f.entity || '', f.broker || '', f.total,
    ].join('|');
  };
  const subsetKey = (subset) => subset.map(flowKey).sort().join('~');

  /**
   * Every subset that could settle custody row *ci*, best first.
   *
   * Unlike :func:`attemptMatch` this ignores the score, so a bundle that loses
   * to a single flow is still offered. The joint allocator needs the losers:
   * handing a wire its second-best option is how the wire behind it gets to
   * settle at all. Ordered by size, then broker match, then content, and
   * capped so a pathological group cannot make the search unbounded.
   */
  const enumerateSubsets = (ci, tol, used, cap, budget) => {
    const c = custodyRows[ci];
    const cAbs = Math.abs(c.amt);
    const cTime = custodyTimes[ci];
    const cDescL = (c.desc || '').toLowerCase();
    const wireType = c.amt > 0 ? 'Payoff' : 'Premium';
    const out = [];
    // Bundles found so far, across every group. The scan callbacks below run synchronously
    // inside scanSubsetsOfSize, and all of them count into this one object.
    const found = { bundles: 0 };
    let spent = false;

    // Every group this wire could draw on, gathered first. Single flows are a
    // linear scan and are enumerated from ALL of them before any bundle work:
    // letting a group that fills the bundle cap end the loop meant the easy
    // candidates in later groups were never seen, and which group came first
    // was just the order the file happened to list its flows in.
    const eligible = [];
    for (const group of flowGroups.values()) {
      if (group.type !== wireType) continue;
      const avail = group.idxs.filter((i) => !used[i]);
      if (!avail.length) continue;
      if (Math.abs(flowTimes[avail[0]] - cTime) > windowMs) continue;
      const brokerMatch = Boolean(group.broker) && cDescL.includes(group.broker);
      for (const i of avail) {
        const d = Math.abs(Math.abs(milFlows[i].total) - cAbs);
        if (d <= tol) out.push({ subset: [i], brokerMatch, exact: d <= CASH_TOL });
      }
      eligible.push({ avail, brokerMatch });
    }

    for (const { avail, brokerMatch } of eligible) {
      if (spent) break;
      const pool = avail.filter((i) => Math.abs(milFlows[i].total) <= cAbs + CASH_TOL);
      // If everything in the group put together cannot reach the wire, no
      // subset of it can either. Without this a big group of small flows --
      // twenty-four 5k trades against a 900k wire -- burns the whole budget
      // enumerating combinations that were never going to sum to anything.
      const poolTotal = pool.reduce((acc, i) => acc + Math.abs(milFlows[i].total), 0);
      if (poolTotal < cAbs - CASH_TOL) continue;
      // Bounds on what a bundle of each size can sum to. Amounts are positive,
      // so the k smallest are the cheapest a k-bundle can be and the k largest
      // the dearest: once the k smallest overshoot the wire, no larger bundle
      // can fit either. Without this the search grinds through every size up
      // to the group's length -- sixty-four 100k flows against a 100k wire ran
      // sixty-three sizes that two flows had already ruled out.
      const sorted = pool.map((i) => Math.abs(milFlows[i].total)).sort((x, y) => x - y);
      const cheapest = [0];
      const dearest = [0];
      for (let k = 0; k < sorted.length; k += 1) {
        cheapest.push(cheapest[k] + sorted[k]);
        dearest.push(dearest[k] + sorted[sorted.length - 1 - k]);
      }
      for (let size = 2; size <= pool.length; size += 1) {
        if (cheapest[size] > cAbs + CASH_TOL) break;
        if (dearest[size] < cAbs - CASH_TOL) continue;
        scanSubsetsOfSize(milFlows, pool, size, cAbs, budget, (combo) => {
          // Bundles are only ever built to CASH_TOL, so they are always exact.
          out.push({ subset: [...combo], brokerMatch, exact: true });
          found.bundles += 1;
          // Stop AT the cap. Collecting every combination and checking the cap
          // afterwards made the bound decorative -- twenty 10k flows against a
          // 100k wire built 184,756 of them before anyone looked.
          return found.bundles < cap;
        });
        if (found.bundles >= cap || budget.steps >= budget.limit) { spent = true; break; }
      }
    }

    return out.sort((a, b) => (
      Number(b.exact) - Number(a.exact)
      || a.subset.length - b.subset.length
      || Number(b.brokerMatch) - Number(a.brokerMatch)
      || (subsetKey(a.subset) < subsetKey(b.subset) ? -1 : 1)
    ));
  };

  const pending = [];
  const nearPass = [];

  const claim = (ci, subset) => {
    subset.forEach((i) => { flowUsed[i] = true; });
    custodyUsed[ci] = true;
    pending.push([ci, subset]);
  };

  // Custody rows whose match was undecidable, with the subset they were given.
  const tiedClaims = [];
  // Flow -> the trades it was interchangeable with. A candidate no wire took
  // is still a genuine break -- the wires carried only so much cash -- but
  // which trade carries it is not knowable, so the row says what it was
  // weighed against instead of pretending the choice was determinate.
  const interchangeableWith = new Map();

  /**
   * Take a match, tied or not, at this custody row's turn in the pass.
   *
   * A tie takes ONE subset -- chosen by content, not by array position -- and
   * leaves the rest for whoever else matched them: consuming every candidate
   * turned a second identical wire into a false MISSING_CP. Deferring the
   * whole decision instead was worse still, because it let a later unique
   * bundle swallow the flows two earlier wires were competing for and starve
   * them both.
   */
  const take = (ci, match) => {
    if (!match.ambiguous) {
      claim(ci, match.subset);
      return;
    }
    const { pool, subset: choice } = match;
    for (const i of pool) {
      const others = interchangeableWith.get(i) || new Set();
      pool.forEach((j) => others.add(j));
      interchangeableWith.set(i, others);
    }
    choice.forEach((i) => { flowUsed[i] = true; });
    custodyUsed[ci] = true;
    tiedClaims.push([ci, choice, pool, Boolean(match.straddles)]);
  };

  /**
   * Undo a wire-by-wire decision that stranded another wire.
   *
   * Taking each wire's best subset at its own turn is fast and, when every
   * wire ends up matched, optimal -- nothing can beat matching all of them.
   * It is not optimal when a wire is left with nothing: preferring a single
   * flow over a bundle can hand one wire the only flow the next wire could
   * have used, when the first could have taken the bundle and left both
   * settled.
   *
   * So this runs only in that case, over only the wires involved, and only
   * replaces the greedy answer when it settles strictly more of them. Bounded
   * three ways -- component size, candidate subsets per wire, and nodes
   * searched -- and it keeps the greedy answer rather than guessing if any
   * bound is hit.
   */
  function repairBlockedWires() {
    /**
     * Cheap necessary conditions, checked before any subset search.
     *
     * Reshuffling can only help a wire if another wire is holding a flow it
     * could use: if every relevant flow is already free, this wire saw them at
     * its own turn and still failed. And if every relevant flow put together
     * cannot reach the wire amount, no subset of them will either. Both are
     * O(n) and they keep the search off the common case -- a custody row with
     * no matching flow at all, which is most unmatched rows in a real book.
     */
    const couldBeHelped = (ci) => {
      const c = custodyRows[ci];
      const cAbs = Math.abs(c.amt);
      const cTime = custodyTimes[ci];
      const wireType = c.amt > 0 ? 'Payoff' : 'Premium';
      let holdsRelevant = false;
      let reachable = 0;
      for (const group of flowGroups.values()) {
        if (group.type !== wireType) continue;
        if (Math.abs(flowTimes[group.idxs[0]] - cTime) > windowMs) continue;
        for (const i of group.idxs) {
          const amt = Math.abs(milFlows[i].total);
          // TOLERANCE_VAL, not CASH_TOL: a single flow may settle a wire it
          // overshoots by up to the near tolerance, and counting only flows at
          // or below the wire wrote those matches off before the search looked.
          if (amt > cAbs + TOLERANCE_VAL) continue;
          reachable += amt;
          if (flowUsed[i]) holdsRelevant = true;
        }
      }
      return holdsRelevant && reachable >= cAbs - TOLERANCE_VAL;
    };

    // Enumerated ONCE per wire, with its own budget, and reused everywhere:
    // the cluster graph, the search, and this filter all read the same list.
    //
    // The list is either COMPLETE -- every subset that wire could take -- or
    // the wire is marked and its whole cluster is left alone. Everything the
    // clustering claims rests on that: a truncated list can hide an exact
    // bundle behind sixty-four near-misses, or omit a flow that ties two
    // clusters into one. A per-wire budget also stops one pathological group
    // from spending the allowance every other cluster needed.
    const candidates = new Map();
    const optionsFor = (ci) => {
      if (candidates.has(ci)) return candidates.get(ci);
      // Its own budget, not a share of the book's. A cumulative allowance made
      // one cluster's repair depend on how expensive the wires before it
      // happened to be, which is not a property of the cluster at all.
      const budget = { steps: 0, limit: PROBE_PER_WIRE };
      // Both tiers, exact ones first. Preferring the exact match by DISCARDING
      // the near ones left a wire holding the only flow another wire could use
      // with a perfectly good near match going spare -- the preference belongs
      // in the order the search tries them, not in what it is allowed to see.
      // Keeping one list also keeps the clustering and the search in agreement
      // about what each wire can take.
      const found = enumerateSubsets(ci, TOLERANCE_VAL, NONE_USED, SUBSET_CAP, budget);
      candidates.set(ci, found);
      return found;
    };

    const stranded = custodyOrder.filter(
      (ci) => !custodyUsed[ci] && couldBeHelped(ci) && optionsFor(ci).length > 0,
    );
    if (!stranded.length) return;

    // The wires that could free something up: those holding flows a stranded
    // wire wanted, plus the stranded wires themselves. Each is tracked with
    // the flows it touches, so they can be split into the separate problems
    // they usually are.
    const touches = new Map();
    const addTouch = (ci, flows) => {
      const set = touches.get(ci) || new Set();
      flows.forEach((i) => set.add(i));
      touches.set(ci, set);
    };
    // Grown to a fixed point rather than in one pass. A wire holding a flow
    // that ANY member could use joins the problem, and it brings its own
    // options with it, which can pull in the wire holding one of those, and so
    // on. Stopping at the wires the stranded ones wanted left a member's
    // alternatives held by outsiders: the search would filter them out as
    // unavailable and conclude the member had nowhere else to go.
    const claims = [...pending, ...tiedClaims.map(([ci, sub]) => [ci, sub])];
    const members = new Set(stranded);
    const wanted = new Set();

    const admit = (ci, claim) => {
      const opts = optionsFor(ci);
      if (claim && !opts.some((o) => subsetKey(o.subset) === subsetKey(claim))) {
        // Its current claim goes in whether or not enumeration found it, so a
        // wire can never lose a match it already held to a truncated list. It
        // has to carry its exactness too: scored as a near match, an exact
        // claim looked worse than it was and the search could trade it for an
        // allocation that books a break.
        const broker = (milFlows[claim[0]].broker || '').toLowerCase().trim();
        const gap = Math.abs(
          claim.reduce((acc, i) => acc + Math.abs(milFlows[i].total), 0)
          - Math.abs(custodyRows[ci].amt),
        );
        opts.unshift({
          subset: claim,
          brokerMatch: Boolean(broker) && (custodyRows[ci].desc || '').toLowerCase().includes(broker),
          exact: gap <= CASH_TOL,
        });
      }
      if (claim) addTouch(ci, claim);
      for (const o of opts) {
        addTouch(ci, o.subset);
        for (const i of o.subset) wanted.add(i);
      }
    };

    stranded.forEach((ci) => admit(ci, null));
    for (;;) {
      const joining = claims.filter(
        ([ci, sub]) => !members.has(ci) && sub.some((i) => wanted.has(i)),
      );
      if (!joining.length) break;
      for (const [ci, sub] of joining) {
        members.add(ci);
        admit(ci, sub);
      }
    }

    // Two wires belong to the same problem only if they are competing for the
    // same flow. Pooling every repairable wire in the book into one set meant
    // seven unrelated two-wire problems looked like one fourteen-wire problem
    // and all seven were abandoned for being too big to search.
    const parent = new Map();
    [...touches.keys()].forEach((ci) => parent.set(ci, ci));
    const find = (x) => {
      let r = x;
      while (parent.get(r) !== r) r = parent.get(r);
      let walk = x;
      while (parent.get(walk) !== r) { const next = parent.get(walk); parent.set(walk, r); walk = next; }
      return r;
    };
    const claimedBy = new Map();
    touches.forEach((flows, ci) => flows.forEach((i) => {
      if (!claimedBy.has(i)) { claimedBy.set(i, ci); return; }
      const a = find(claimedBy.get(i));
      const b = find(ci);
      if (a !== b) parent.set(a, b);
    }));
    const clusters = new Map();
    [...touches.keys()].forEach((ci) => {
      const root = find(ci);
      if (!clusters.has(root)) clusters.set(root, []);
      clusters.get(root).push(ci);
    });

    [...clusters.values()]
      .map((wires) => wires.sort((a, b) => custodyTimes[a] - custodyTimes[b] || a - b))
      .sort((a, b) => custodyTimes[a[0]] - custodyTimes[b[0]] || a[0] - b[0])
      .forEach((cluster) => reallocate(cluster, candidates));
  }

  /**
   * Re-lay one cluster of wires competing for the same flows.
   *
   * Skipping an oversized cluster costs only that cluster: the others are
   * separate problems and are searched on their own.
   */
  function reallocate(component, candidates) {
    if (component.length < 2 || component.length > COMPONENT_CAP) return;
    if (component.every((ci) => custodyUsed[ci])) return;
    // A truncated candidate list is safe to act on. The search only ever picks
    // from the same lists the clustering was built from, so a cluster cannot
    // reach a flow another cluster is using no matter how much enumeration was
    // cut short -- what is lost is a possible improvement, never correctness.
    // Refusing to act instead turned an ordinary blocked pair into false
    // breaks whenever some unrelated group nearby was expensive to enumerate.

    // Flows the rest of the book is holding stay held; the cluster reshuffles
    // only what its own members claimed.
    const outside = new Array(milFlows.length).fill(false);
    flowUsed.forEach((used, i) => { outside[i] = used; });
    const componentSet = new Set(component);
    const releaseFrom = (claims) => claims
      .filter(([ci]) => componentSet.has(ci))
      .forEach(([, sub]) => sub.forEach((i) => { outside[i] = false; }));
    releaseFrom(pending);
    releaseFrom(tiedClaims);

    // Exactly the list the clustering was built from, narrowed to what the
    // rest of the book has left free. Nothing is enumerated here, so the
    // search cannot reach a flow the clustering did not account for.
    const options = component.map((ci) => ({
      ci,
      subsets: candidates.get(ci).filter((o) => o.subset.every((i) => !outside[i])),
    }));

    // Contended flows last. A wire that can settle from a flow nobody else
    // wants should do that and leave the contested one to the wire with no
    // other choice -- which is the whole point of allocating jointly. Trying
    // the exact match first instead sends the search down the one branch that
    // cannot work and it backtracks through the entire cluster to find out.
    const demand = new Map();
    for (const { subsets } of options) {
      const seen = new Set();
      for (const o of subsets) {
        for (const i of o.subset) {
          if (seen.has(i)) continue;
          seen.add(i);
          demand.set(i, (demand.get(i) || 0) + 1);
        }
      }
    }
    const contention = (o) => o.subset.reduce((acc, i) => acc + (demand.get(i) || 0), 0);
    for (const opt of options) {
      opt.subsets.sort((a, b) => (
        contention(a) - contention(b)
        || Number(b.exact) - Number(a.exact)
        || a.subset.length - b.subset.length
        || Number(b.brokerMatch) - Number(a.brokerMatch)
        || (subsetKey(a.subset) < subsetKey(b.subset) ? -1 : 1)
      ));
    }
    // Most constrained wire first, so a dead end shows up before the search
    // has committed thirteen other wires to it.
    options.sort((a, b) => (
      a.subsets.length - b.subsets.length
      || custodyTimes[a.ci] - custodyTimes[b.ci]
      || a.ci - b.ci
    ));

    // Equally good means same size and same broker match. A bundle standing in
    // for a single is an alternative the allocator weighed, not an ambiguity:
    // it was chosen because it settles another wire, which is a reason.
    const gapOf = (ci, subset) => Math.abs(
      subset.reduce((acc, i) => acc + Math.abs(milFlows[i].total), 0) - Math.abs(custodyRows[ci].amt),
    );
    // Two near matches a different distance out are not the same answer: one
    // books a $10 break and the other $40. Calling them interchangeable
    // reported AMBIGUOUS with no difference at all and hid the smaller break.
    const peersOf = (opt, sub) => opt.subsets.filter(
      (o) => o.subset.length === sub.subset.length
        && o.brokerMatch === sub.brokerMatch
        && o.exact === sub.exact
        && Math.abs(gapOf(opt.ci, o.subset) - gapOf(opt.ci, sub.subset)) <= TIE_EPS,
    );

    const greedyCount = component.filter((ci) => custodyUsed[ci]).length;
    const budget = { steps: 0 };
    const taken = new Set();
    const chosen = [];
    let best = null;

    /**
     * Settling more wires wins outright; between allocations that settle the
     * same wires, the one that books fewer breaks wins.
     *
     * Without the second half the search stopped at whichever full allocation
     * it reached first, which the contention order makes the one built from
     * the least wanted flows -- so a $30 near single could be taken while an
     * exact bundle sat unused, booking a VAL_BREAK that need not exist.
     */
    const scoreOf = () => {
      const picks = chosen.filter(Boolean);
      let exact = 0;
      let broker = 0;
      let diff = 0;
      let flows = 0;
      let key = '';
      for (const [ci, sub, , isExact, isBrokerMatch] of picks) {
        if (isExact) exact += 1;
        if (isBrokerMatch) broker += 1;
        diff += Math.abs(
          sub.reduce((acc, i) => acc + Math.abs(milFlows[i].total), 0) - Math.abs(custodyRows[ci].amt),
        );
        flows += sub.length;
        key += `${ci}:${subsetKey(sub)}|`;
      }
      return { count: picks.length, exact, broker, diff, flows, key };
    };
    const isBetter = (a, b) => {
      if (b === null) return true;
      if (a.count !== b.count) return a.count > b.count;
      if (a.exact !== b.exact) return a.exact > b.exact;
      if (Math.abs(a.diff - b.diff) > TIE_EPS) return a.diff < b.diff;
      // A wire naming its broker is evidence about which flow it settled, and
      // it outranks the content key, which is only there to make the choice
      // repeatable rather than to mean anything.
      if (a.broker !== b.broker) return a.broker > b.broker;
      // FEWER flows, not more. One payment settling one trade is the more
      // likely reading and it is what the matcher prefers everywhere else;
      // counting matched rows instead let a two-flow bundle beat an equally
      // exact single and book the unused single as a break.
      if (a.flows !== b.flows) return a.flows < b.flows;
      return a.key < b.key;
    };

    const walk = (k, count) => {
      if (budget.steps > ALLOC_BUDGET) return true;
      // Two ceilings: a branch that cannot beat greedy is pointless, and one
      // that cannot even match the best count found so far cannot win on
      // quality either. Equal counts stay in play, which is what lets a better
      // allocation of the same wires be found.
      const ceiling = count + (options.length - k);
      if (ceiling <= greedyCount) return false;
      if (best !== null && ceiling < best.count) return false;
      if (k === options.length) {
        const score = scoreOf();
        if (isBetter(score, best)) {
          best = { ...score, chosen: chosen.filter(Boolean).map((x) => [...x]) };
        }
        // Every wire settled, every match exact, nothing booked as a break:
        // there is nothing left to improve, so stop rather than enumerate the
        // rest of the cluster's arrangements to prove it.
        // No early exit. Stopping at the first all-exact allocation ignored
        // the rest of the score -- two arrangements can settle every wire
        // exactly and still disagree about which trades they name, and the
        // wire's own description is evidence about that. A cluster that could
        // stop early now costs ~150ms instead of ~6ms, and the budget still
        // bounds the rest.
        return false;
      }
      for (const opt of options[k].subsets) {
        budget.steps += 1;
        if (opt.subset.some((i) => taken.has(i))) continue;
        opt.subset.forEach((i) => taken.add(i));
        chosen[k] = [
          options[k].ci, opt.subset, peersOf(options[k], opt).length > 1, opt.exact, opt.brokerMatch,
        ];
        const stop = walk(k + 1, count + 1);
        opt.subset.forEach((i) => taken.delete(i));
        chosen[k] = null;
        if (stop) return true;
      }
      return walk(k + 1, count);
    };
    walk(0, 0);

    // A spent budget is not a reason to discard a better allocation that was
    // already found -- it only means no better one was proven to exist.
    if (best === null || best.count <= greedyCount) return;

    // Better: drop the component's greedy claims and re-lay them.
    const dropped = (claims) => claims.filter(([ci]) => !componentSet.has(ci));
    pending.splice(0, pending.length, ...dropped(pending));
    tiedClaims.splice(0, tiedClaims.length, ...dropped(tiedClaims));
    component.forEach((ci) => { custodyUsed[ci] = false; });
    flowUsed.forEach((_, i) => { flowUsed[i] = outside[i]; });
    for (const [ci, sub, wasTied] of best.chosen) {
      if (wasTied) {
        const opt = options.find((o) => o.ci === ci);
        const key = subsetKey(sub);
        const mine = opt.subsets.find((o) => subsetKey(o.subset) === key);
        const peers = peersOf(opt, mine);
        const pool = flowsIn(peers.map((o) => o.subset));
        for (const i of pool) {
          const others = interchangeableWith.get(i) || new Set();
          pool.forEach((j) => others.add(j));
          interchangeableWith.set(i, others);
        }
        // Judged on each peer's total, for the same reason the greedy pass
        // does: a bundle's parts are all smaller than the wire.
        const target = Math.abs(custodyRows[ci].amt);
        const sums = peers.map(
          (o) => o.subset.reduce((acc, i) => acc + Math.abs(milFlows[i].total), 0),
        );
        const straddles = sums.some((v) => v > target + TIE_EPS)
          && sums.some((v) => v < target - TIE_EPS);
        sub.forEach((i) => { flowUsed[i] = true; });
        custodyUsed[ci] = true;
        tiedClaims.push([ci, sub, pool, straddles]);
      } else {
        claim(ci, sub);
      }
    }
  }

  for (const ci of custodyOrder) {
    const match = attemptMatch(ci, CASH_TOL);
    if (match === null) nearPass.push(ci);
    else take(ci, match);
  }
  for (const ci of nearPass) {
    const match = attemptMatch(ci, TOLERANCE_VAL);
    if (match !== null) take(ci, match);
  }

  repairBlockedWires();

  const ambiguousResults = [];
  for (const [ci, choice, alternatives, straddles] of tiedClaims) {
    const members = choice.map((i) => milFlows[i]);
    // Which trade settled is unknown; how far the wire is out is not. Every
    // tied candidate is the same distance from it -- that is what made them a
    // tie -- so the difference is definite even when the identity is not, and
    // reporting nothing hid a real break from anyone reading the numbers.
    const cAbs = Math.abs(custodyRows[ci].amt);
    const diffTotal = members.reduce((acc, m) => acc + Math.abs(m.total), 0) - cAbs;
    // Every tied candidate is the same distance out, but not necessarily on
    // the same side: $99,990 and $100,010 are both $10 from a $100,000 wire.
    // Where they straddle it the size of the break is known and its direction
    // is not, and printing one sign or the other is a coin toss dressed as a
    // number. Whether they straddle is decided where the candidates are still
    // whole subsets, not here where only their flows remain.
    for (const m of members) {
      ambiguousResults.push({
        // AMBIGUOUS, not VAL_BREAK: the status card, the status filter and the
        // export all key off this, and an unknown identity is what these rows
        // are. The break rides in the difference beside it.
        mil: m, custody: [custodyRows[ci]], status: 'AMBIGUOUS', ambiguous: true,
        diff: straddles ? null : diffTotal, wireId: null,
        siblingFlows: siblingSummary(members),
        // Every trade the wire could have settled, so the dialog shows what the
        // choice was between and not only the side that happened to be taken.
        siblingTradeIds: allTradeIds(alternatives.map((i) => milFlows[i])),
      });
    }
  }

  pending.sort((a, b) => custodyTimes[a[0]] - custodyTimes[b[0]]);

  const results = [];
  let wireCounter = 0;
  for (const [ci, memberIdx] of pending) {
    const c = custodyRows[ci];
    const cAbs = Math.abs(c.amt);
    wireCounter += 1;
    const wireId = `WIRE-${String(wireCounter).padStart(4, '0')}`;

    const members = memberIdx.map((i) => milFlows[i]);
    const siblingTradeIds = allTradeIds(members);
    const siblingFlows = siblingSummary(members);
    const wireTotal = members.reduce((acc, m) => acc + Math.abs(m.total), 0);
    const diffTotal = wireTotal - cAbs;
    const status = Math.abs(diffTotal) <= CASH_TOL ? 'MATCHED' : 'VAL_BREAK';
    for (const m of members) {
      results.push({
        mil: m, custody: c, status, ambiguous: false,
        diff: diffTotal, wireId, siblingFlows, siblingTradeIds,
      });
    }
  }

  results.push(...ambiguousResults);

  flowUsed.forEach((used, fi) => {
    if (used) return;
    const alternatives = interchangeableWith.get(fi);
    results.push({
      mil: milFlows[fi],
      custody: null,
      ...emptyWire,
      // A flow that lost a tie is still unsettled -- two wires cannot settle
      // three flows -- so the break stands. What is uncertain is which of the
      // interchangeable trades carries it.
      siblingTradeIds: alternatives
        ? allTradeIds([...alternatives].map((i) => milFlows[i]))
        : [],
    });
  });
  custodyUsed.forEach((used, ci) => {
    if (!used) {
      results.push({
        mil: null, custody: custodyRows[ci], status: 'MISSING_CP',
        ambiguous: false, diff: null, wireId: null,
        siblingFlows: [], siblingTradeIds: [],
      });
    }
  });

  return results;
}

/** Drop the Date field the UI never reads, so results stay JSON-safe. */
function stripDate(o) {
  if (o == null) return null;
  const { date, ...rest } = o;
  return rest;
}

export function serializeCashResults(results) {
  return results.map((r) => ({
    mil: stripDate(r.mil),
    custody: Array.isArray(r.custody) ? r.custody.map(stripDate) : stripDate(r.custody),
    status: r.status,
    ambiguous: r.ambiguous ?? false,
    diff: r.diff ?? null,
    wireId: r.wireId ?? null,
    siblingFlows: r.siblingFlows || [],
    siblingTradeIds: r.siblingTradeIds || [],
  }));
}

/**
 * The wires a trade of one flow type settled on, newest wire last.
 *
 * Tolerates the pre-list index shape so a stale payload cannot break the grids.
 */
export function wireIdsFor(tradeWireIndex, flowType, tradeId) {
  const tid = tradeId == null ? '' : String(tradeId).trim();
  if (!tid || !tradeWireIndex) return [];
  const hit = tradeWireIndex[`${flowType}|${tid}`];
  if (!hit) return [];
  return Array.isArray(hit) ? hit : [hit];
}

/**
 * Build {wires: {wireId: {...}}, tradeWireIndex: {"Premium|<id>": [wireId]}} from
 * already-serialized cash results, so the recon grids can show which wire each
 * trade settled on.
 */
export function buildWiresIndex(serializedCashResults) {
  const wires = {};
  const tradeWireIndex = {};
  for (const r of serializedCashResults) {
    const wid = r.wireId;
    if (!wid) continue;
    const mil = r.mil || {};
    for (const tid of mil.tradeIds || []) {
      if (!tid) continue;
      // A list, not a single id: flows are keyed per settlement date, so one
      // trade of one type can settle across several wires. Overwriting left
      // the grids and the export showing whichever came last.
      const key = `${mil.type}|${tid}`;
      if (!tradeWireIndex[key]) tradeWireIndex[key] = [];
      if (!tradeWireIndex[key].includes(wid)) tradeWireIndex[key].push(wid);
    }
    if (wires[wid]) continue;
    const custody = r.custody;
    const custodyOne = custody && !Array.isArray(custody) ? custody : null;
    const members = r.siblingFlows || [];
    wires[wid] = {
      wireId: wid,
      custody: custodyOne,
      members,
      tradeIds: r.siblingTradeIds || [],
      total: members.reduce((acc, m) => acc + Math.abs(m.total || 0), 0),
      diff: r.diff ?? null,
      dateStr: custodyOne ? custodyOne.dateStr ?? null : null,
      amt: custodyOne ? custodyOne.amt ?? null : null,
      source: custodyOne ? custodyOne.source ?? null : null,
      acctNum: custodyOne ? custodyOne.acctNum ?? null : null,
      desc: custodyOne ? custodyOne.desc ?? null : null,
    };
  }
  return { wires, tradeWireIndex };
}
